import '@sapudex/web-components/dist/BusyIndicator.js';
import '@sapudex/web-components/dist/Button.js';
import '@sapudex/web-components/dist/Link.js';
import '@ui5/webcomponents-icons/dist/slim-arrow-left.js';
import '@ui5/webcomponents-icons/dist/slim-arrow-right.js';
import '@ui5/webcomponents-icons/dist/zoom-in.js';
import { LIGHTBOX_TYPE } from '../../../../libs/lightbox/lightbox.constants.js';
import {
  closeLightbox,
  openLightbox,
} from '../../../../libs/lightbox/utils/dispatch-lightbox-events.js';
import { createOptimizedPicture } from '../../../../scripts/aem.js';
import {
  a,
  button,
  div,
  domEl,
  h3,
  h4,
  span,
  udex,
} from '../../../../scripts/dom-builder.js';
import getConfig, {
  getBaseDomain,
} from '../../../../scripts/utils/config-utils.js';
import { isExternalCDNEnv } from '../../../../scripts/utils/env-utils.js';
import isExternal from '../../../../scripts/utils/link-utils.js';
import { localizeUrl } from '../../../../scripts/utils/url-utils.js';
import {
  BUTTON_TYPES,
  BUTTONS_ATTRIBUTES_MAP,
  HANDRAISER_BUTTON_PATHS,
  HANDRAISER_BUTTON_TYPES,
  IMAGE_DIMENSIONS,
  VIEW_TYPES,
} from '../product-tour.constants.js';
import formatImagePath from './format-image-path.js';
import updateImagePath from './update-image-path.js';

export const UNTITLED_SECTION_PLACEHOLDER_KEY =
  'MXP.Section.Title.Untitled.Label';

// Constants for repeated strings
const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';
const MODAL_HEADING_ID = 'modal-heading';
const PRODUCT_TOUR_SELECTOR = '.product-tour';

/**
 * Safely sanitize a string by removing unwanted characters.
 * This approach avoids regex backtracking vulnerabilities.
 * @param {string} str - String to sanitize
 * @returns {string} - Sanitized string
 */
function sanitizeString(str) {
  if (!str || typeof str !== 'string') {
    return '';
  }
  return Array.from(str)
    .map((char) => (/^[a-zA-Z0-9._-]$/.test(char) ? char : ''))
    .join('');
}
const DESKTOP_BREAKPOINT = 1280;
const ARIA_EXPANDED_ATTR = 'aria-expanded';
const MOBILE_ACCORDION_CONTENT_SELECTOR =
  '.gt_mobile .gt_open .gt_accordion-content';

/**
 * Analytics attributes
 */
export const AA_CONSTANTS = {
  REGION: 'body|product-tour',
  MEDIA_TYPE: 'demo',
  MEDIA_TITLE_DESKTOP: 'Guided Product Tour (desktop)',
  MEDIA_TITLE_MOBILE: 'Guided Product Tour (mobile)',
  LINKS: {
    ENLARGE_SCREENSHOT: 'Enlarge screenshot',
    OVERVIEW: 'Overview',
    START_TOUR: 'Start the Tour',
    START_TOUR_AGAIN: 'Start tour again',
    NEXT_STEP: 'Next Step',
    PREVIOUS_STEP: 'Previous Step',
    REQUEST_DEMO: 'Request a demo',
    TRY_NOW: 'Try now',
  },
};

/**
 * Fetches content fragment data by path.
 * @param {string} path - The path of the content fragment.
 * @returns {Promise<Object|null>} - The fetched content fragment data or null on failure.
 */
export async function fetchContentFragmentByPath(path) {
  if (!path) {
    return null;
  } // Return null if path is not provided
  const API_PATH = getConfig('fragement.api.path');
  const BASE_DOMAIN = getBaseDomain();
  const API_BASE_URL = `${BASE_DOMAIN}${API_PATH}`;
  const apiUrl = `${API_BASE_URL}?path=${encodeURIComponent(path)}`;
  try {
    const response = await fetch(apiUrl, {
      credentials: 'omit',
    });

    if (!response.ok) {
      // eslint-disable-next-line no-console
      console.warn(`Failed to fetch content fragment: ${response.status}`);
      return null;
    }
    return await response.json();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Error fetching content fragment:', error);
    return null;
  }
}

/**
 * Sanitizes URL parts by converting to lowercase and replacing special characters
 * while preserving file extensions
 * @param {string} item - URL part to sanitize
 * @returns {string} Sanitized URL part
 */
export function sanitizeEdsUrl(item) {
  if (!item || typeof item !== 'string') {
    return '';
  }
  return item
    .split('/')
    .map((part) => {
      // Check if the part has a file extension
      const lastDotIndex = part.lastIndexOf('.');
      if (lastDotIndex !== -1) {
        const name = part.substring(0, lastDotIndex);
        const ext = part.substring(lastDotIndex);
        // Sanitize only the filename part, preserve the extension
        return sanitizeString(name.toLowerCase()) + ext.toLowerCase();
      }
      // No extension, sanitize the whole part
      return sanitizeString(part.toLowerCase());
    })
    .join('/');
}

export function getLinkParams(data, url) {
  const {
    intent = '',
    product = '',
    productName = data.title,
    pageTitle = 'Product Tour',
    countryOfOrigin = 'en_us',
    refererPagePath = url.origin + url.pathname,
    refererContentPath = url.pathname.replace('/trial.html', '/product-tour'),
  } = data.metadataParams ?? {};

  return {
    intent,
    product,
    productName,
    pageTitle,
    countryOfOrigin,
    refererPagePath,
    refererContentPath,
    navTitle: data.display_text,
  };
}

/**
 * Format the demo URL with all required parameters
 * @param {Object} data - The data object containing title and call_to_action information
 * @returns {string} The constructed demo URL
 */
export function formatUrl(data) {
  // Extract URL from the JSON object
  const urlString = data?.url;

  // Return fallback URL if no urlString exists
  if (!urlString) {
    return '#';
  }

  try {
    // Use URL object to parse the URL
    const url = new URL(urlString);

    if (isExternalCDNEnv()) {
      url.hostname = window.location.hostname;
    }

    const params = getLinkParams(data, url);

    Object.entries(params).forEach(([key, value]) => {
      if (value) {
        url.searchParams.set(key, value);
      }
    });

    return localizeUrl(url.href);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Invalid URL in call_to_action:', error);

    return '#';
  }
}

// SVG Decorators
export function decorateNextSVG() {
  return domEl('ui5-icon', {
    name: 'slim-arrow-right',
    accessibleName: 'Navigate to next step',
  });
}

export function decoratePreviousSVG() {
  return domEl('ui5-icon', {
    name: 'slim-arrow-left',
    accessibleName: 'Navigate to previous step',
  });
}

export function decorateRightCaretSVG() {
  const svg = document.createElementNS(SVG_NAMESPACE, 'svg');
  svg.setAttribute('viewBox', '0 0 6 10');
  svg.setAttribute('xmlns', SVG_NAMESPACE);

  const path = document.createElementNS(SVG_NAMESPACE, 'path');
  path.setAttribute(
    'd',
    'M0.800005 9.99994C0.590005 9.99994 0.380005 9.91994 0.220005 9.74994C-0.0899951 9.42994 -0.0699963 8.91994 0.250004 8.61994L4.04 4.99994L0.250004 1.37994C-0.0699963 1.06994 -0.0799951 0.569939 0.220005 0.249939C0.530005 -0.0700608 1.03 -0.0800608 1.35 0.219939L5.75 4.41994C5.91 4.56994 6 4.77994 6 4.99994C6 5.21994 5.91 5.42994 5.75 5.57994L1.35 9.77994C1.2 9.92994 1 9.99994 0.800005 9.99994Z',
  );

  svg.appendChild(path);
  return svg;
}

/**
 * Creates a "Start tour again" link with proper structure and analytics attributes
 * @param {boolean} isDesktop - Whether this is for desktop view
 * @returns {Element} The link element with proper structure
 */
export function createStartTourAgainLink(isDesktop = false) {
  const mediaTitle = isDesktop
    ? AA_CONSTANTS.MEDIA_TITLE_DESKTOP
    : AA_CONSTANTS.MEDIA_TITLE_MOBILE;

  return a(
    {
      href: '#',
      class: 'gt_link gt_focus gt_modal-info-start-again',
      'aria-label': AA_CONSTANTS.LINKS.START_TOUR_AGAIN,
      'data-analytics-aa-tracking': 'true',
      'data-analytics-aa-region': AA_CONSTANTS.REGION,
      'data-analytics-aa-mediatype': AA_CONSTANTS.MEDIA_TYPE,
      'data-analytics-aa-mediatitle': mediaTitle,
      'data-analytics-aa-linkname': AA_CONSTANTS.LINKS.START_TOUR_AGAIN,
      'data-analytics-aa-linktitle': AA_CONSTANTS.LINKS.START_TOUR_AGAIN,
    },
    AA_CONSTANTS.LINKS.START_TOUR_AGAIN,
    span({ class: 'gt_link-icon gt_link-icon-right' }, decorateRightCaretSVG()),
  );
}

/**
 * Handles the enlarge button click event
 * @param {Element} container - The container element
 * @param {boolean} isDesktop - Whether the view is desktop (true) or mobile (false)
 * @param {string} sectionTitle - Title of the current active section
 */
async function handleEnlargeClick(container, isDesktop, sectionTitle) {
  const selector = isDesktop
    ? '.gt_desktop .col-9 .gt_content-container picture'
    : '.gt_accordion.gt_open .gt_content-container picture';

  const currentPicture = container
    .closest(PRODUCT_TOUR_SELECTOR)
    .querySelector(selector);

  if (!currentPicture) {
    return;
  }

  openLightbox(LIGHTBOX_TYPE.image, {
    header: {
      headline: sectionTitle || '',
    },
    content: {
      image: currentPicture.cloneNode(true),
    },
    className: ['product-tour-lightbox', 'lightbox__grid-width'],
    isLoading: false,
    attributes: {
      ariaLabel: 'Product Tour Screenshot',
    },
  });
}

/**
 * Creates a demo button with analytics attributes
 * @param {Object} data - The data object containing CTA information
 * @param {boolean} isDesktop - Flag indicating if it's desktop view
 * @param {object} options - Additional options for each button type
 * @returns {Element} The demo button element
 */
function createButtonByType(data, isDesktop, options) {
  const idPrefix = isDesktop ? '_D' : '_M';
  const buttonLabel = data.display_text;
  const buttonUrl = formatUrl(data);

  return a(
    {
      id: `${options.id}${idPrefix}`,
      class: `gt_button ${data.buttonTypeClassName} contact-us-raq-lightbox gt_request-handraiser-button`,
      'aria-label': buttonLabel,
      'data-analytics-aa-region': AA_CONSTANTS.REGION,
      'data-analytics-aa-tracking': 'true',
      'data-analytics-aa-linkname': buttonLabel,
      'data-analytics-aa-mediatype': AA_CONSTANTS.MEDIA_TYPE,
      'data-analytics-aa-mediatitle': isDesktop
        ? AA_CONSTANTS.MEDIA_TITLE_DESKTOP
        : AA_CONSTANTS.MEDIA_TITLE_MOBILE,
      'data-analytics-aa-linktitle': buttonLabel,
      'data-analytics-aa-destinationurl': buttonUrl,
      type: 'button',
      role: 'button',
      href: buttonUrl,
    },
    buttonLabel,
  );
}

/**
 * Creates a visit product button with analytics attributes
 * @param {Object} data - The data object containing CTA information
 * @param {boolean} isDesktop - Flag indicating if it's desktop view
 * @returns {Element} The visit button element
 */
function createCustomButton(data, isDesktop) {
  const idPrefix = isDesktop ? '_D' : '_M';
  const { display_text: buttonLabel, url: buttonUrl } = data;

  const customButtonAttributes = {
    id: `customButton${idPrefix}`,
    class: `gt_button ${data.buttonTypeClassName} gt_register-custom-button`,
    target: isExternal(buttonUrl) ? '_blank' : '_self',
    rel: isExternal(buttonUrl) ? 'noopener noreferrer' : '',
    'data-analytics-aa-region': AA_CONSTANTS.REGION,
    'data-analytics-aa-destinationurl': buttonUrl,
    'data-analytics-aa-tracking': 'true',
    'data-analytics-aa-linkname': buttonLabel,
    'aria-label': buttonLabel,
    'data-analytics-aa-mediatype': AA_CONSTANTS.MEDIA_TYPE,
    'data-analytics-aa-mediatitle': isDesktop
      ? AA_CONSTANTS.MEDIA_TITLE_DESKTOP
      : AA_CONSTANTS.MEDIA_TITLE_MOBILE,
    'data-analytics-aa-linktitle': buttonLabel,
  };

  const customButton = button(
    { ...customButtonAttributes, type: 'button' },
    buttonLabel,
  );

  customButton.addEventListener('click', () => {
    window.open(buttonUrl, '_blank', 'noopener,noreferrer');

    if (window.adobeDataLayer) {
      window.adobeDataLayer.push({
        event: 'exit',
        exit: {
          link: buttonUrl,
          title: buttonLabel,
        },
      });
    }
  });

  return customButton;
}

/**
 * Returns action buttons sorted by explicit CTA order while preserving input order
 * fallback for items without `cta_order`.
 *
 * @param {Array<Object>} buttons - Raw CTA objects from fragment data.
 * @returns {Array<Object>} CTA objects enriched with `index`, sorted by `cta_order` (or original index).
 */
function getSortedActionButtons(buttons = []) {
  return buttons
    .map((cta, index) => ({
      ...cta,
      index,
    }))
    .sort((a, b) => (a.cta_order ?? a.index) - (b.cta_order ?? b.index));
}

/**
 * Creates buttons for user actions
 * @param {Object} fragmentData - The data object containing call_to_action configuration
 * @param {boolean} isDesktop - Whether the view is desktop (true) or mobile (false)
 * @param {string} containerClass - Class for the buttons container
 * @returns {Element} Container with action buttons
 */
export function createButtonsContainer(
  fragmentData,
  isDesktop,
  containerClass,
) {
  const buttonsContainer = div({ class: containerClass });
  const contactUsMetadataHref = document.querySelector(
    '.contact-module-meta',
  )?.href;

  const metadataParams = contactUsMetadataHref
    ? Object.fromEntries(
        new URLSearchParams(new URL(contactUsMetadataHref).searchParams),
      )
    : {};
  const sortedActionButtons = getSortedActionButtons(
    fragmentData.call_to_action,
  );

  if (sortedActionButtons?.length) {
    sortedActionButtons.forEach((data, index) => {
      if (!data.url || !data.display_text) {
        return;
      }

      if (!data.type || data.type === 'null') {
        const matched = Object.entries(HANDRAISER_BUTTON_PATHS).find(
          ([, path]) => data.url.includes(path),
        );

        if (matched) {
          data.type = matched[0];
        }
      }

      const buttonTypeClassName =
        index === 0 ? 'gt_button-primary' : 'gt_button-secondary';
      const buttonData = {
        ...data,
        buttonTypeClassName,
        metadataParams,
        title: fragmentData.title,
      };
      const additionalButtonData = BUTTONS_ATTRIBUTES_MAP[buttonData.type];
      const button = additionalButtonData
        ? createButtonByType(buttonData, isDesktop, additionalButtonData)
        : createCustomButton(buttonData, isDesktop);

      buttonsContainer.appendChild(button);
    });
  } else {
    // Without call_to_action data, only "Request a demo" button is created
    const requestDemoPath =
      HANDRAISER_BUTTON_PATHS[HANDRAISER_BUTTON_TYPES.DEMO];
    const demoUrl =
      document.querySelector(
        `.contentFragmentReference .formattedUrls a[href*="${requestDemoPath}"]`,
      )?.href || `${window.location.origin}${requestDemoPath}`;
    const buttonData = {
      url: demoUrl,
      type: BUTTON_TYPES.DEMO,
      display_text: 'Request a Demo',
      buttonTypeClassName: 'gt_button-primary',
    };
    const additionalButtonData = BUTTONS_ATTRIBUTES_MAP[BUTTON_TYPES.DEMO];

    const demoButton = createButtonByType(
      buttonData,
      isDesktop,
      additionalButtonData,
    );

    buttonsContainer.appendChild(demoButton);
  }

  return buttonsContainer;
}

/**
 * Creates the extra section with heading and buttons
 * @param {Object} data - The data object containing title and call_to_action information
 * @param {boolean} isDesktop - Whether the view is desktop (true) or mobile (false)
 * @returns {Element} The extra section element
 */
export function createExtraSection(data, isDesktop) {
  const containerClass = isDesktop
    ? 'gt_desktop-extra'
    : 'col-12 gt_mobile-extra';

  // Create enlarge button using UDEx button component (AC 4)
  const enlargeButton = udex(
    'button',
    {
      design: 'Tertiary',
      endIcon: 'zoom-in',
      size: 'Large',
      class: 'gt_enlarge',
      'data-analytics-aa-tracking': 'true',
      'data-analytics-aa-region': AA_CONSTANTS.REGION,
      'data-analytics-aa-mediatitle': isDesktop
        ? AA_CONSTANTS.MEDIA_TITLE_DESKTOP
        : AA_CONSTANTS.MEDIA_TITLE_MOBILE,
      'data-analytics-aa-mediatype': AA_CONSTANTS.MEDIA_TYPE,
      'data-analytics-aa-linkname': AA_CONSTANTS.LINKS.ENLARGE_SCREENSHOT,
      'data-analytics-aa-linktitle': AA_CONSTANTS.LINKS.ENLARGE_SCREENSHOT,
      onClick: async (e) => {
        const container = e.target.closest(
          '.gt_desktop-extra, .gt_mobile-extra',
        );
        if (container) {
          const activeNavBtnSelector = isDesktop
            ? '.gt_nav.gt_active .gt_nav-btn h3'
            : '.gt_accordion.gt_open .gt_accordion-btn p';
          const activeNavBtn = container
            .closest(PRODUCT_TOUR_SELECTOR)
            ?.querySelector(activeNavBtnSelector);
          const sectionTitle = activeNavBtn?.textContent ?? '';
          await handleEnlargeClick(container, isDesktop, sectionTitle);
        }
      },
    },
    'Enlarge',
  );

  // Create the extra section container
  return div(
    { class: containerClass },
    div(enlargeButton),
    h4(
      data.call_to_action_title ||
        `Take the next step and learn how ${data.title} can meet your business needs.`,
    ),
    createButtonsContainer(data, isDesktop, `${containerClass}_buttons`),
  );
}

/**
 * Creates the section with buttons
 * @param {Object} data - Data object
 * @param {boolean} isDesktop - Whether the view is desktop
 * @returns {Element} The section element
 */
export function createModalButtons(data, isDesktop) {
  const containerClass = isDesktop
    ? 'gt_desktop-extra_buttons'
    : 'gt_mobile-extra_buttons';

  return createButtonsContainer(data, isDesktop, containerClass);
}

/**
 * Utility: create modal content (title, buttons, info)
 * @param {Object} data - Section data
 * @param {boolean} isDesktop - Whether the view is desktop (true) or mobile (false)
 */
export function buildModalActionsContent(data, isDesktop) {
  const modalContent = div({ class: 'gt_custom-modal-content' });

  if (!isDesktop) {
    const dialogHeading = h3(
      {
        id: MODAL_HEADING_ID,
        tabindex: '-1',
      },
      'Ready to take the next step?',
    );
    modalContent.append(dialogHeading);
  }

  modalContent.append(createModalButtons(data, isDesktop));

  const startAgainLink = createStartTourAgainLink(isDesktop);

  // Add click handler for "Start tour again" link
  startAgainLink.addEventListener('click', (e) => {
    e.preventDefault();
    handleStartTourAgain(isDesktop, data);
  });

  const modalInfo = div(
    { class: 'gt_modal-info' },
    div({ class: 'gt_content-modal_link' }, startAgainLink),
  );
  modalContent.append(modalInfo);
  return modalContent;
}

/**
 * Handle "Start tour again" click - closes modal and navigates to second section
 * @param {boolean} isDesktop - Whether this is desktop view
 */

/**
 * Close desktop section and reset its state
 * @param {Element} section - Section element to close
 */
export function closeDesktopSection(section) {
  section.classList.remove('gt_active');
  const content = section.querySelector('.gt_nav-content');
  const navButton = section.querySelector('.gt_nav-btn');
  if (content) {
    content.setAttribute('hidden', '');
    content.removeAttribute('style');
  }
  if (navButton) {
    navButton.setAttribute(ARIA_EXPANDED_ATTR, 'false');
  }
}

/**
 * Close mobile section and reset its state
 * @param {Element} section - Section element to close
 */
function closeMobileSection(section) {
  section.classList.remove('gt_open');
  const content = section.querySelector('.gt_accordion-content');
  const accordionButton = section.querySelector('.gt_accordion-btn');
  if (content) {
    content.setAttribute('hidden', '');
  }
  if (accordionButton) {
    accordionButton.setAttribute(ARIA_EXPANDED_ATTR, 'false');
  }
}

/**
 * Open desktop section and set focus
 * @param {Element} section - Section element to open
 */
export function openDesktopSection(section) {
  const content = section.querySelector('.gt_nav-content');
  const sectionNavButton = section.querySelector('.gt_nav-btn');
  if (content) {
    content.removeAttribute('hidden');
  }
  if (sectionNavButton) {
    sectionNavButton.click();
    sectionNavButton.setAttribute(ARIA_EXPANDED_ATTR, 'true');
    sectionNavButton.focus();
  }
}

/**
 * Open mobile section and set focus
 * @param {Element} section - Section element to open
 */
export function openMobileSection(section) {
  const content = section.querySelector('.gt_accordion-content');
  const sectionAccordionButton = section.querySelector('.gt_accordion-btn');
  if (content) {
    content.removeAttribute('hidden');
  }
  if (sectionAccordionButton) {
    sectionAccordionButton.click();
    sectionAccordionButton.setAttribute(ARIA_EXPANDED_ATTR, 'true');
    sectionAccordionButton.focus();
  }
}

/**
 * Close all sections except the target section
 * @param {NodeList} sections - All tour sections
 * @param {Element} targetSection - Section to keep open
 * @param {boolean} isDesktop - Whether it's desktop view
 */
function closeOtherSections(sections, targetSection, isDesktop) {
  // eslint-disable-next-line no-restricted-syntax
  for (const section of sections) {
    if (section !== targetSection) {
      if (isDesktop) {
        closeDesktopSection(section);
      } else {
        closeMobileSection(section);
      }
    }
  }
}

/**
 * Update tour image for manual restart
 * @param {Element} section - Target section
 * @param {Object} data - Tour data
 * @param {boolean} isDesktop - Whether it's desktop view
 */
function updateTourImageManually(section, data, isDesktop) {
  const sectionIndex = Number.parseInt(section.dataset.section, 10);

  if (!Number.isNaN(sectionIndex) && data?.section?.[sectionIndex]) {
    const sectionData = data.section[sectionIndex];
    const firstStep = sectionData?.step?.[0];

    if (firstStep?.screen) {
      const imageContainer = isDesktop
        ? section.querySelector('.gt_desktop .col-9 .gt_content-container')
        : section.querySelector('.gt_content-container');

      if (imageContainer) {
        updateImageWithLoader(
          imageContainer,
          firstStep.screen,
          firstStep.screen.fileName || 'Product tour step',
          IMAGE_DIMENSIONS.DESKTOP_WIDTH,
          IMAGE_DIMENSIONS.MOBILE_WIDTH,
        );
      }
    }
  }
}

/**
 * Perform manual tour restart when button click fails
 * @param {boolean} isDesktop - Whether it's desktop view
 * @param {Object} data - Tour data
 */
function performManualRestart(isDesktop, data) {
  const selector = isDesktop
    ? '.gt_desktop .gt_nav'
    : '.gt_mobile .gt_accordion';
  const sections = document.querySelectorAll(selector);

  if (sections.length <= 1) {
    return;
  }

  const currentActiveSelector = isDesktop
    ? '.gt_desktop .gt_nav.gt_active'
    : '.gt_mobile .gt_accordion.gt_open';
  const currentActiveSection = document.querySelector(currentActiveSelector);
  const secondSection = sections[1];

  if (!currentActiveSection || !secondSection) {
    return;
  }

  // Close current section
  if (isDesktop) {
    closeDesktopSection(currentActiveSection);
  } else {
    closeMobileSection(currentActiveSection);
  }

  closeOtherSections(sections, secondSection, isDesktop);

  // Open second section
  if (isDesktop) {
    openDesktopSection(secondSection);
  } else {
    openMobileSection(secondSection);
  }

  updateTourImageManually(secondSection, data, isDesktop);
  secondSection.dataset.step = '1';
}

function handleStartTourAgain(isDesktop, data) {
  // Close the modal
  closeModalIfOpen(isDesktop ? VIEW_TYPES.DESKTOP : VIEW_TYPES.MOBILE);
  performManualRestart(isDesktop, data);
}

/**
 * Utility: hide elements for mobile modal
 */
function hideMobileElements() {
  const openAccordionContent = document.querySelector(
    MOBILE_ACCORDION_CONTENT_SELECTOR,
  );
  if (openAccordionContent) {
    openAccordionContent.style.display = 'none';
  }

  const mobileExtra = document.querySelector('.gt_mobile-extra');
  if (mobileExtra) {
    mobileExtra.style.display = 'none';
  }
}

/**
 * Utility: show modal for mobile inside gt_content-container
 */
function showMobileModal(data) {
  // Check if mobile modal already exists
  const existingMobileModal = document.querySelector(
    '.gt_custom-modal-mobile-parent',
  );
  if (existingMobileModal) {
    existingMobileModal.style.display = 'block';
    hideMobileElements();
    return;
  }

  hideMobileElements();

  const gtContentContainer = document.querySelector(
    MOBILE_ACCORDION_CONTENT_SELECTOR,
  );

  if (gtContentContainer) {
    gtContentContainer.style.display = 'none';
  }

  // Insert modalParent into .gt_mobile .gt_open
  const gtMobileOpen = document.querySelector('.gt_mobile .gt_open');

  if (gtMobileOpen) {
    openLightbox(LIGHTBOX_TYPE.message, {
      header: {
        headline: '',
        hideCloseIcon: true,
      },
      content: {
        description: buildModalActionsContent(data, false),
      },
      isLoading: false,
      attributes: {
        ariaLabelledby: MODAL_HEADING_ID,
      },
      containerElement: gtMobileOpen,
      className: 'gt_custom-modal-mobile-parent',
    });
  }
}

/**
 * Utility: show modal for desktop with overlay
 */
function showDesktopModal(data) {
  const container = document.querySelector('.gt_desktop .gt_content-container');
  if (!container) {
    return;
  }

  // Always hide .gt_desktop-extra when showing modal
  const desktopExtra = document.querySelector('.gt_desktop-extra');
  if (desktopExtra) {
    desktopExtra.style.display = 'none';
  }

  openLightbox(LIGHTBOX_TYPE.message, {
    header: {
      headline: 'Ready to take the next step?',
      hideCloseIcon: true,
    },
    content: {
      description: buildModalActionsContent(data, true),
    },
    isLoading: false,
    containerElement: container,
  });
}

/**
 * Sets focus to the first interactive element (Request a Demo button)
 * @param {boolean} isDesktop - Whether it's desktop view
 */
function setModalFocus(isDesktop) {
  const selector = isDesktop
    ? '.gt_custom-modal-overlay [id^="requestDemo"]'
    : '.gt_custom-modal-mobile-parent [id^="requestDemo"]';

  const demoButton = document.querySelector(selector);
  if (demoButton) {
    demoButton.focus();
  }
}

/**
 * Open modal with actions (desktop: overlay, mobile: white container)
 * @param {Object} data - Section data
 */
export function openModalWithActions(data) {
  const isDesktop = window.innerWidth >= DESKTOP_BREAKPOINT;
  if (isDesktop) {
    showDesktopModal(data);
  } else {
    showMobileModal(data);
  }

  // Always set focus to modal heading after opening
  setModalFocus(isDesktop);
}

/**
 * Utility to hide a modal window if it is currently open
 * @param {string} viewType - The type of view (desktop or mobile)
 */
export function closeModalIfOpen(viewType) {
  closeLightbox();

  // Restore .gt_desktop-extra when closing modal
  if (viewType === VIEW_TYPES.DESKTOP) {
    const desktopExtra = document.querySelector('.gt_desktop-extra');
    if (desktopExtra) {
      desktopExtra.style.display = 'block';
    }
  }

  if (viewType === VIEW_TYPES.MOBILE) {
    // Restore .gt_mobile-extra
    const mobileExtra = document.querySelector('.gt_mobile-extra');
    if (mobileExtra) {
      mobileExtra.style.display = '';
    }

    // Restore accordion content
    const gtContentContainer = document.querySelector(
      MOBILE_ACCORDION_CONTENT_SELECTOR,
    );
    if (gtContentContainer) {
      gtContentContainer.style.display = '';
    }
  }
}

/**
 * Extracts the best available alt text from step item data
 * Priority: fileAltText > fileName > fallback
 * @param {Object} stepItem - Step item with screen data
 * @param {string} fallback - Fallback text if no alt text available
 * @returns {string} The best available alt text
 */
export function extractAltText(stepItem, fallback = '') {
  return (
    stepItem?.screen?.fileAltText || stepItem?.screen?.fileName || fallback
  );
}

/**
 * Sanitizes step description HTML — allows plain text and at most one <a> tag.
 * Unwraps a single root <p> element so the result can be safely injected into
 * a <p> container without creating invalid nested <p> elements.
 * Any additional anchor tags beyond the first are removed to meet requirements.
 * @param {string} html - Raw HTML string from step description
 * @returns {string} Sanitized HTML string
 */
export function sanitizeStepDescription(html) {
  if (!html) {
    return '';
  }
  const wrapper = div();
  wrapper.innerHTML = html;
  const links = wrapper.querySelectorAll('a');
  links.forEach((link, index) => {
    if (index > 0) {
      link.replaceWith(link.textContent);
    }
  });
  // Unwrap a single root <p> to avoid <p> inside <p> when injected into a <p> container
  const children = Array.from(wrapper.childNodes);
  if (children.length === 1 && children[0].nodeName === 'P') {
    return children[0].innerHTML;
  }
  return wrapper.innerHTML;
}

/**
 * Appends a busy indicator loader to the image container if not already present.
 * Adds `loading` class and removes it once the contained image fires load or error.
 * @param {Element} imageContainer - The container element to append the loader to
 */
function setLoadingState(imageContainer, isLoading) {
  if (isLoading) {
    imageContainer.classList.add('loading');
  } else {
    imageContainer.classList.remove('loading');
  }
}

export function appendLoaderToContainer(imageContainer) {
  if (!imageContainer || imageContainer.querySelector('.gt_loader')) {
    return;
  }
  const loader = div(
    { class: 'gt_loader', inert: '' },
    udex('busy-indicator', { size: 'M', active: '' }),
  );
  imageContainer.append(loader);

  const img = imageContainer.querySelector('img');
  if (!img) {
    return;
  }
  setLoadingState(imageContainer, true);

  const removeLoading = () => setLoadingState(imageContainer, false);
  if (img.complete) {
    removeLoading();
  } else {
    img.addEventListener('load', removeLoading, { once: true });
    img.addEventListener('error', removeLoading, { once: true });
  }
}

/**
 * Updates an image container with a new image, showing a loading state
 * @param {Element} imageContainer - The container element for the image
 * @param {Object} imageData - The image data object containing fileUrl and fileType
 * @param {string} altText - Alternative text for the image
 * @param {number} [desktopWidth] - Width for desktop images (defaults to desktop dimensions)
 * @param {number} [mobileWidth] - Width for mobile images (defaults to mobile dimensions)
 */
export function updateImageWithLoader(
  imageContainer,
  imageData,
  altText,
  desktopWidth = IMAGE_DIMENSIONS.DESKTOP_WIDTH,
  mobileWidth = IMAGE_DIMENSIONS.MOBILE_WIDTH,
) {
  if (!imageContainer) {
    return;
  }

  const imagePath = formatImagePath(imageData?.fileUrl);
  const isPlaceholder = !imageData?.fileUrl;

  // Create optimized picture element
  const newPicture = createOptimizedPicture(imagePath, altText, false, [
    { media: '(min-width: 640px)', width: desktopWidth.toString() },
    { width: mobileWidth.toString() },
  ]);

  updateImagePath(newPicture);

  // Ensure loader element exists, then show loading state for new image
  appendLoaderToContainer(imageContainer);
  setLoadingState(imageContainer, true);

  // Add placeholder class if needed
  if (isPlaceholder) {
    newPicture.querySelector('img').classList.add('gt_placeholder');
  }

  // Add gt_image class
  newPicture.querySelector('img').classList.add('gt_image');

  // Replace existing picture or append new one
  const currentPicture = imageContainer.querySelector('picture');
  if (currentPicture) {
    currentPicture.replaceWith(newPicture);
  } else {
    imageContainer.append(newPicture);
  }

  // Remove loading state once image is loaded
  const imgLoading = newPicture.querySelector('img');
  if (imgLoading.complete) {
    setLoadingState(imageContainer, false);
  } else {
    imgLoading.onload = () => setLoadingState(imageContainer, false);
    imgLoading.onerror = () => setLoadingState(imageContainer, false);
  }
}
